'use client';
import React, { useEffect, useRef } from 'react';

interface PageHeroProps {
  children: React.ReactNode;
  className?: string;
}

export default function PageHero({ children, className = '' }: PageHeroProps) {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Immediately animate all hero elements — they are always above the fold
    const el = heroRef.current;
    if (!el) return;
    const items = el.querySelectorAll('.animate-on-scroll');
    // Use a tiny timeout so CSS can apply before the class flip
    const t = setTimeout(() => {
      items.forEach((item) => item.classList.add('is-visible'));
    }, 30);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      ref={heroRef}
      className={`relative overflow-hidden ${className}`}
      style={{ background: 'linear-gradient(135deg, #075DDF 0%, #0066FF 45%, #3B82F6 100%)' }}
    >
      {/* Grid lines */}
      <div className="absolute inset-0 grid-lines opacity-20" />

      {/* Orb top-right */}
      <div
        className="orb absolute w-[600px] h-[600px] rounded-full opacity-15 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 65%)', top: '-180px', right: '-180px' }}
      />
      {/* Orb bottom-left */}
      <div
        className="orb-2 absolute w-[350px] h-[350px] rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #0046AF 0%, transparent 70%)', bottom: '-80px', left: '-80px' }}
      />

      {/* Animated beam columns */}
      <div className="absolute inset-0 flex justify-between pointer-events-none px-8 md:px-20 lg:px-32">
        <div className="relative w-px h-full bg-white/[0.12] overflow-hidden">
          <div className="beam" />
        </div>
        <div className="relative w-px h-full bg-white/[0.12] overflow-hidden hidden md:block">
          <div className="beam beam-delay-2" />
        </div>
        <div className="relative w-px h-full bg-white/[0.12] overflow-hidden">
          <div className="beam beam-delay-3" />
        </div>
      </div>

      {/* Content — positioned above decorations */}
      <div className="relative z-10">
        {children}
      </div>

      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, rgba(59, 130, 246,0.2))' }}
      />
    </div>
  );
}